import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import Layout from "../../layout/Layout";
import {
  fetchUnitsAPI,
  fetchOrdersAPI,
  cancelPurchaseOrderAPI,
  cancelPurchaseOrderOTPAPI,
  fetchPoAPI,
} from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MaterialReactTable } from "material-react-table";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";

import { apiData } from "../../services/api_service";
import { formatAED, formatNumber } from "../../utils/format";
import moment from "moment";
import CancelIcon from "@mui/icons-material/Cancel";
import { WarningAmber } from "@mui/icons-material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

function PurchaseOrderList1() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const [totalCount, setTotalCount] = useState(0);
  const [poList, setPOList] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(15);
  const [resendVisible, setResendVisible] = useState(false);

  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState(location.state?.filters || []);
  const [sorting, setSorting] = useState([]);
  const [unitsList, setUnitsList] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  const handleEditStore = (id) => {
    // Plain client-side navigation — the edit page already refetches by
    // its :id route param, so a full page reload isn't needed here.
    navigate(`/purchase-order/edit/${id}`);
  };

  let tempRefs = useRef(false);
  const inputRefs = useRef([]);

  const fetchOrders = useCallback(async () => {
    const payload = {
      pageIndex: pageIndex,
      pageSize: pageSize,
      sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
      search: filters.length > 0 ? JSON.stringify(filters) : null,
    };
    try {
      const response = await apiData("get", fetchPoAPI, payload);
      setPOList(response.data.result);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, sorting, filters]);

  useEffect(() => {
    if (tempRefs.current === false) {
      fetchOrders();
    }
  }, [fetchOrders]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (location.state?.filters) {
      setFilters(location.state.filters);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchUnits = async () => {
      const data = {
        intParentId: 88,
      };

      try {
        const result = await apiData("get", fetchUnitsAPI, data);
        if (result.success) {
          setUnitsList(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchUnits();
  }, []);

  const handleOpenDialog = (order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
  };

  const handleSendOtp = async () => {
    try {
      if (!selectedOrder) return;
      setSendingOtp(true);

      const payload = {
        po_id: selectedOrder.po_id,
        po_number: selectedOrder.po_no,
        created_user: user.emp_id,
        created_name: user.username,
      };

      const result = await apiData("post", cancelPurchaseOrderOTPAPI, payload);

      if (result.success) {
        setOpenDialog(false);
        setOtpDialogOpen(true);
        setTimer(15 * 60);
        setResendVisible(false);
      } else {
        alert(result.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Send OTP failed:", error);
      alert("Error occurred while sending OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  useEffect(() => {
    let interval = null;
    if (otpDialogOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendVisible(true);
    }
    return () => clearInterval(interval);
  }, [otpDialogOpen, timer]);

  const handleResendOtp = () => {
    handleSendOtp(); // same API call
  };

  const handleSubmitOtp = async () => {
    try {
      setVerifyingOtp(true);
      const payload = {
        po_id: selectedOrder.po_id,
        po_number: selectedOrder.po_no,
        created_user: user.emp_id,
        cancel_otp: otp,
      };

      const result = await apiData("post", cancelPurchaseOrderAPI, payload);

      if (result.success) {
        alert("Order cancelled successfully.");
        setOtpDialogOpen(false);
        fetchOrders();
      } else {
        alert(result.message || "Invalid OTP or expired.");
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      alert("Error occurred while verifying OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      // Always the whole list: pageIndex 0 (a later page would skip rows).
      const payload = {
        pageIndex: 0,
        pageSize: 10000000,
        sorting: sorting.length > 0 ? JSON.stringify(sorting[0]) : null,
        search: filters.length > 0 ? JSON.stringify(filters) : null,
      };

      const response = await apiData("get", fetchPoAPI, payload);
      const dataArray = Array.isArray(response.data.result)
        ? response.data.result
        : [];
      if (dataArray.length === 0) {
        alert("No data to export!");
        return;
      }

      const money = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

      // One row per PO item, so each item's quantity and price sit in their own
      // numeric cells. The PO-level amounts are written once, on the PO's first
      // row, so a column total never counts a PO twice.
      const rowSpans = []; // number of rows in each PO's block
      const exportData = dataArray.flatMap((item, index) => {
        const poItems = Array.isArray(item.items) ? item.items : [];
        rowSpans.push(Math.max(1, poItems.length));
        const itemsTotal =
          poItems.reduce(
            (acc, i) => acc + parseFloat(i.total_amount_without_gst || i.total_price || 0),
            0,
          ) || 0;
        const subtotal =
          (item.subtotal || item.total_without_gst) && parseFloat(item.subtotal || item.total_without_gst) > 0
            ? parseFloat(item.subtotal || item.total_without_gst)
            : itemsTotal;
        const tax =
          (item.tax_amount || item.total_vat) && parseFloat(item.tax_amount || item.total_vat) > 0
            ? parseFloat(item.tax_amount || item.total_vat)
            : Math.max(0, itemsTotal - parseFloat(item.discount_amount || 0)) * 0.05;
        const shipping = parseFloat(
          item.transport_charge || item.transport_amount || item.shipping_amount || 0,
        );
        const grand =
          (item.grand_total || item.total_with_gst) && parseFloat(item.grand_total || item.total_with_gst) > 0
            ? parseFloat(item.grand_total || item.total_with_gst)
            : Math.max(0, itemsTotal - parseFloat(item.discount_amount || 0)) * 1.05 +
              parseFloat(item.transport_charge || item.shipping_amount || 0);
        const header = {
          "#": index + 1,
          "PO No.": item.po_no || "",
          "PO Date": item.po_date ? moment(item.po_date).format("DD-MM-YYYY") : "",
          "Created At": item.created_at ? moment(item.created_at).format("DD-MM-YYYY") : "",
          "Created By": item.created_by_name || "",
          Receipt: item.order_receipt || "",
          "Vendor Name": item.vendor_name || "",
          "Vendor Company": item.vendor_company_name || "",
          Phone: item.vendor_phone || "",
          Email: item.vendor_email || "",
          "Vendor VAT No": item.vendor_vat_no || "",
          "Vendor Address": [item.vendor_address, item.vendor_emirate].filter(Boolean).join(", "),
          Store: item.store_name || "",
          Status: item.order_status || "",
        };
        const totals = {
          Subtotal: money(subtotal),
          Tax: money(tax),
          Shipping: money(shipping),
          Discount: money(item.discount_amount),
          "Grand Total": money(grand),
        };
        if (!poItems.length) {
          return [{ ...header, "Product Name": "", SKU: "", Unit: "", Quantity: "", "Price (Per Qty)": "", "Line Total": "", ...totals }];
        }
        return poItems.map((it, i) => ({
          ...header,
          "Product Name": it.product_name || "",
          SKU: it.sku || "",
          Unit: it.name || "",
          Quantity: parseFloat(it.quantity) || 0,
          "Price (Per Qty)": money(it.buying_price),
          "Line Total": money(it.total_price),
          ...(i === 0 ? totals : {}),
        }));
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      // One block per PO: only the product columns get a row per item; every
      // other cell (PO No., dates, vendor, receipt, totals...) is merged down
      // across the PO's items instead of repeating.
      {
        const perItem = ["Product Name", "SKU", "Unit", "Quantity", "Price (Per Qty)", "Line Total"];
        const keys = Object.keys(exportData[0] || {});
        ws["!merges"] = ws["!merges"] || [];
        let rowIdx = 1; // row 0 is the header
        rowSpans.forEach((len) => {
          if (len > 1) {
            keys.forEach((k, c) => {
              if (!perItem.includes(k)) ws["!merges"].push({ s: { r: rowIdx, c }, e: { r: rowIdx + len - 1, c } });
            });
          }
          rowIdx += len;
        });
      }
      const wb = XLSX.utils.book_new();
      // Money columns: real numbers shown as AED 1,234.50.
      const moneyHeaders = ["Price (Per Qty)", "Line Total", "Subtotal", "Tax", "Shipping", "Discount", "Grand Total"];
      const headerRow = Object.keys(exportData[0] || {});
      moneyHeaders.forEach((h) => {
        const c = headerRow.indexOf(h);
        if (c < 0) return;
        for (let r = 1; r <= exportData.length; r += 1) {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (cell && cell.t === "n") cell.z = '"AED" #,##0.00';
        }
      });
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "purchase_order_list");

      const fileName = `purchase_order_list_${moment().format("DD_MM_YYYY")}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "po_no",
        header: "PO No.",
        size: 50,
      },
      {
        accessorKey: "po_date",
        header: "PO Date",
        size: 50,
        Cell: ({ cell }) => dayjs(cell.getValue()).format("DD-MM-YYYY"),
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                clearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "white" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 50,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "NA",
      },
      {
        accessorKey: "created_by_name",
        header: "Created By",
        size: 50,
        Cell: ({ cell }) => cell.getValue() || "NA",
      },
      {
        accessorKey: "order_receipt",
        header: "Receipt",
        size: 50,
        enableSorting: false,
        Cell: ({ cell }) => {
          const url = cell.getValue();
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "90px",
                height: "30px",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RemoveRedEyeIcon color="blue" fontSize="small" />
            </a>
          ) : (
            <span>No Receipt</span>
          );
        },
      },
      {
        accessorKey: "vendor_name",
        header: "Vendor Name",
        size: 50,
      },
      {
        accessorKey: "vendor_phone",
        header: "Phone",
        size: 50,
      },
      {
        accessorKey: "vendor_email",
        header: "Email",
        size: 200,
      },
      {
        accessorKey: "store_name",
        header: "Store",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "order_status",
        header: "Status",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "product_name",
        header: "Ordered Products",
        size: 250,
        Cell: ({ row }) =>
          row.original.items.map((item) => (
            <div key={item.order_item_id}>
              {item.product_name} ({item.quantity} {item.name})
            </div>
          )),
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
        size: 50,
        Cell: ({ row }) =>
          row.original.items.map((item) => (
            <div key={item.order_item_id}>{formatNumber(item.quantity)}</div>
          )),
      },
      {
        accessorKey: "buying_price",
        header: "Price (Per Qty)",
        size: 50,
        Cell: ({ row }) =>
          row.original.items.map((item) => (
            <div key={item.order_item_id}>
              {formatAED(item.buying_price)}
            </div>
          )),
      },
      {
        accessorKey: "total_without_gst",
        header: "Subtotal",
        size: 50,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const storedSubtotal = parseFloat(
            row.original.subtotal || row.original.total_without_gst || 0,
          );
          if (storedSubtotal > 0) return formatAED(storedSubtotal);

          // Fallback calculation using correct field name for PO items
          const calculated =
            row.original.items?.reduce(
              (acc, i) =>
                acc +
                parseFloat(i.total_amount_without_gst || i.total_price || 0),
              0,
            ) || 0;
          return formatAED(calculated);
        },
      },
      {
        accessorKey: "total_vat",
        header: "Tax",
        size: 50,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const storedTax = parseFloat(row.original.tax_amount || row.original.total_vat || 0);
          if (storedTax > 0) return formatAED(storedTax);

          const subtotal =
            row.original.items?.reduce(
              (acc, i) =>
                acc +
                parseFloat(i.total_amount_without_gst || i.total_price || 0),
              0,
            ) || 0;
          const discount = parseFloat(row.original.discount_amount || 0);
          const tax = Math.max(0, subtotal - discount) * 0.05;
          return formatAED(tax);
        },
      },
      {
        accessorKey: "transport_charge",
        header: "Shipping",
        size: 50,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return formatAED(
            row.original.transport_charge ||
              row.original.transport_amount ||
              row.original.shipping_amount ||
              0,
          );
        },
      },
      {
        accessorKey: "discount_amount",
        header: "Discount",
        size: 50,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          return formatAED(row.original.discount_amount);
        },
      },
      {
        accessorKey: "total_with_gst",
        header: "Grand Total",
        size: 50,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const storedGrand = parseFloat(row.original.grand_total || row.original.total_with_gst || 0);
          if (storedGrand > 0) return formatAED(storedGrand);

          const subtotal =
            row.original.items?.reduce(
              (acc, i) =>
                acc +
                parseFloat(i.total_amount_without_gst || i.total_price || 0),
              0,
            ) || 0;
          const discount = parseFloat(row.original.discount_amount || 0);
          const netSubtotal = Math.max(0, subtotal - discount);
          const tax = netSubtotal * 0.05;
          const shipping = parseFloat(
            row.original.transport_charge || row.original.shipping_amount || 0,
          );
          const grandTotal = netSubtotal + tax + shipping;
          return formatAED(grandTotal);
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const { order_status, order_id } = row.original;
          return order_status === "Cancelled" ? (
            <Grid container direction="column" alignItems="center">
              <Tooltip title="Purchase Order Cancelled">
                <span style={{ color: "red", fontWeight: "bold" }}>
                  Cancelled
                </span>
              </Tooltip>
            </Grid>
          ) : (
            <Grid container direction="column" alignItems="center">
              <Tooltip title="Cancel Purchase">
                <span>
                  <IconButton
                    onClick={() => handleOpenDialog(row.original)}
                    sx={{
                      background:
                        "linear-gradient(145deg, #f47370ff, #cb3232ff)", // gradient red
                      color: "white",
                      fontSize: "0.85rem",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      boxShadow: "0px 3px 6px rgba(0,0,0,0.2)",
                      textTransform: "none",
                      "&:hover": {
                        background:
                          "linear-gradient(145deg, #981414ff, #9a0007)", // darker hover
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    {" "}
                    Cancel{" "}
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          );
        },
      },
      {
        accessorKey: "edit",
        header: "Edit",
        size: 50,
        enableColumnFilter: false,
        enableSorting: false,

        Cell: ({ row }) => {
          const isCancelled = row.original.order_status === "Cancelled";

          return (
            <Tooltip
              title={
                isCancelled
                  ? "This purchase order is cancelled – editing disabled."
                  : "Click to Edit"
              }
            >
              <span>
                <IconButton
                  color="primary"
                  disabled={isCancelled}
                  onClick={() => {
                    if (!isCancelled) {
                      handleEditStore(row.original.po_id);
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [handleEditStore, navigate],
  );

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
            mb={5}
          >
            <Typography className="page_title" textAlign="left">
              {location.state?.reportTitle || location.state?.title || "Purchase Order List"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Purchase Orders</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
            mb={2}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              onClick={() => navigate("/purchase-order/add")}
              startIcon={<AddIcon />}
            >
              Purchase Order
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Grid>
          <MaterialReactTable
            columns={columns}
            data={poList}
            enableStickyHeader
            enableGlobalFilter={false}
            enableFullScreenToggle={false}
            className="mat-react-tbl"
            state={{
              isLoading: loading,
              pagination: { pageIndex, pageSize },
              sorting,
              columnFilters: filters,
            }}
            manualPagination
            manualSorting
            manualFiltering
            enableRowNumbers
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const paginationState = updater({
                pageIndex,
                pageSize,
              });

              const { pageIndex: newPageIndex, pageSize: newPageSize } =
                paginationState;
              setPageIndex(newPageIndex);
              setPageSize(newPageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 240px)",
                minHeight: 450,
                overflowY: "auto",
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                padding: "3px 8px",
                border: "1px solid #ccc",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                padding: "8px",
                border: "1px solid #ccc",
                color: "#fff !important",
                fontWeight: "bold",
                backgroundColor: "#1976d2", // Added background color to ensure it matches blue theme
                "& svg": {
                  color: "#fff !important",
                },
                "& .MuiTableSortLabel-root": {
                  color: "#fff !important",
                  "&.Mui-active": {
                    color: "#fff !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                    },
                  },
                },
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
                "& .MuiInputBase-input": {
                  color: "#fff !important",
                  textFillColor: "#fff !important",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#fff !important",
                  opacity: 0.8,
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important",
                },
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                borderBottom: "1px solid #ccc",
                backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
              },
            })}
            muiTableHeadCellFilterTextFieldProps={{
              sx: {
                "& input": {
                  color: "#fff !important", // Change text color
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "#fff !important", // Change underline color
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "#fff !important", // Hover underline color
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#fff !important", // Focused underline color
                },
              },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    p: "0.5rem",
                    justifyContent: "space-between",
                    flexGrow: 1,

                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                        // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          />
        </Grid>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmber color="warning" />
          <Typography variant="h6">Cancel Purchase Order</Typography>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              Are you sure you want to cancel purchase order{" "}
              <strong>{selectedOrder.order_number}</strong>? <br />
              An OTP will be sent to the admin for verification.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            variant="outlined"
          >
            No
          </Button>
          <LoadingButton
            onClick={handleSendOtp}
            color="primary"
            variant="contained"
            loading={sendingOtp}
            disabled={sendingOtp}
          >
            Send OTP
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" textAlign="center">
            Enter OTP
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* OTP input boxes */}
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 2 }}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)} // store refs
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "1.2rem",
                    width: "2.5rem",
                    height: "2.5rem",
                    padding: 0,
                  },
                }}
                value={otp[index] || ""}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, ""); // only digits
                  let newOtp = otp.split("");

                  if (val) {
                    newOtp[index] = val;
                    setOtp(newOtp.join(""));
                    if (index < 3) {
                      inputRefs.current[index + 1]?.focus();
                    }
                  } else {
                    // If cleared
                    newOtp[index] = "";
                    setOtp(newOtp.join(""));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace") {
                    e.preventDefault();
                    let newOtp = otp.split("");

                    if (otp[index]) {
                      // Clear current value
                      newOtp[index] = "";
                      setOtp(newOtp.join(""));
                    } else if (index > 0) {
                      // Move to previous
                      inputRefs.current[index - 1]?.focus();
                      let prevOtp = otp.split("");
                      prevOtp[index - 1] = "";
                      setOtp(prevOtp.join(""));
                    }
                  }
                }}
              />
            ))}
          </Box>

          {/* Resend timer */}
          <Typography sx={{ mt: 3, textAlign: "center" }}>
            {resendVisible ? (
              <Button
                onClick={handleResendOtp}
                color="primary"
                variant="text"
                sx={{ textTransform: "none" }}
              >
                Resend OTP
              </Button>
            ) : (
              <>
                {timer >= 60 ? (
                  <>
                    OTP expires in{" "}
                    <strong>
                      {Math.floor(timer / 60)}:
                      {String(timer % 60).padStart(2, "0")}
                    </strong>{" "}
                    minutes
                  </>
                ) : (
                  <>
                    OTP expires in <strong>{timer}</strong> seconds
                  </>
                )}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <LoadingButton
            onClick={handleSubmitOtp}
            color="primary"
            variant="contained"
            size="large"
            loading={verifyingOtp}
            disabled={verifyingOtp}
          >
            Verify
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default PurchaseOrderList1;
